import type { ITeamUpdateCall, TeamUpdateCallStatus } from '../types/TeamUpdate';

export type TeamUpdateCallLeaveResult = {
  participantIds: string[];
  status: TeamUpdateCallStatus;
};

/** True when the group has a live video call with at least one participant. */
export function isTeamUpdateCallLive(
  call: ITeamUpdateCall | null | undefined
): call is ITeamUpdateCall {
  if (!call || call.status !== 'live') return false;
  return Array.isArray(call.participantIds) && call.participantIds.length > 0;
}

/** Presence after hang-up: last leaver (or explicit end) closes the room. */
export function nextTeamUpdateCallAfterLeave(input: {
  participantIds: unknown;
  userId: string;
  endCall?: boolean;
}): TeamUpdateCallLeaveResult {
  const remaining = Array.isArray(input.participantIds)
    ? input.participantIds.map(String).filter((id) => id !== input.userId)
    : [];
  if (input.endCall || remaining.length === 0) {
    return { participantIds: [], status: 'ended' };
  }
  return { participantIds: remaining, status: 'live' };
}

/** Whether the given user is currently listed on the live call. */
export function isUserInTeamUpdateCall(
  call: ITeamUpdateCall | null | undefined,
  userId: string | undefined
): boolean {
  if (!userId || !isTeamUpdateCallLive(call)) return false;
  return call.participantIds.includes(userId);
}

/** Build groupId → live call map from an observed calls record. */
export function liveCallsByGroupId(
  callsByGroupId: Record<string, ITeamUpdateCall | null | undefined>
): Record<string, ITeamUpdateCall> {
  const result: Record<string, ITeamUpdateCall> = {};
  Object.entries(callsByGroupId).forEach(([groupId, call]) => {
    if (isTeamUpdateCallLive(call)) {
      result[groupId] = call;
    }
  });
  return result;
}
